// Settings — the chart of accounts, the entity, member names, and the account
// map the automatic postings use. Accountant terms are fine here.
import { useEffect, useMemo, useState } from 'react';
import { useBooksAccess } from '../../contexts/AuthContext';
import { ConfirmButton, Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { memberWords } from '../../lib/format';
import { accounts as accountsApi, settings } from '../../lib/ledger/api';
import {
  ACCOUNT_TYPE_LABELS, MEMBERS, TAX_LINE_LABELS,
  type Account, type AccountMap, type AccountType, type EntitySettings, type Member, type MemberSettings, type NormalBalance, type TaxLine,
} from '../../lib/ledger/types';
import { AccountSelect, ReadOnlyNote, useAccounts } from './shared';

const TYPE_ORDER: AccountType[] = ['asset', 'liability', 'equity', 'income', 'cogs', 'expense'];
const TAX_LINES = Object.keys(TAX_LINE_LABELS) as TaxLine[];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const DEFAULT_ENTITY: EntitySettings = { name: 'Rootegra LLC', tax_form: '1065', fiscal_year_start: 1, state: null, ein_last4: null };
const DEFAULT_NAMES: MemberSettings = { leah: { name: 'Leah' }, laura: { name: 'Laura Means' } };
const DEFAULT_MAP: AccountMap = {
  vessel: '1000', due_from_apple: '1200', apple_sales: '4000', apple_refunds: '4010', apple_commission: '6100',
  adjustments: '6950', equipment: '1500', accum_depr: '1590', depr_expense: '6600', opening_equity: '3900',
  default_shared_cost: '6900', disposal: '4950',
  capital: { leah: '3010', laura: '3020' }, distributions: { leah: '3110', laura: '3120' },
};

type FlatKey = Exclude<keyof AccountMap, 'capital' | 'distributions'>;
const MAP_ROWS: { key: FlatKey; label: string; hint: string }[] = [
  { key: 'vessel', label: 'Operating cash', hint: 'Where Apple deposits and shared costs post.' },
  { key: 'due_from_apple', label: 'Due from Apple', hint: 'What Apple owes for closed months not yet paid.' },
  { key: 'apple_sales', label: 'Apple sales', hint: 'Gross Academy sales.' },
  { key: 'apple_refunds', label: 'Apple refunds', hint: 'Refunds Apple took back.' },
  { key: 'apple_commission', label: 'Apple commission', hint: "Apple's cut." },
  { key: 'adjustments', label: 'Adjustments', hint: 'Adjustments entered on the Apple tab.' },
  { key: 'default_shared_cost', label: 'Default shared cost', hint: 'Shared costs from a vendor with no account of its own.' },
  { key: 'equipment', label: 'Equipment', hint: 'Default asset account for new equipment.' },
  { key: 'accum_depr', label: 'Accumulated depreciation', hint: 'Contra-asset for depreciation taken.' },
  { key: 'depr_expense', label: 'Depreciation expense', hint: 'Monthly depreciation posts here.' },
  { key: 'disposal', label: 'Gain or loss on disposal', hint: 'Difference when an asset is sold or scrapped.' },
  { key: 'opening_equity', label: 'Opening balance equity', hint: 'Where an unbalanced opening entry lands.' },
];

function normalFor(t: AccountType): NormalBalance {
  return t === 'asset' || t === 'expense' || t === 'cogs' ? 'debit' : 'credit';
}

export function SettingsTab() {
  const { canEdit } = useBooksAccess();
  const chart = useAccounts();
  const [all, setAll] = useState<Record<string, unknown> | null>(null);
  const [settingsErr, setSettingsErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const [actErr, setActErr] = useState<unknown>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setSettingsErr(null);
    settings.all()
      .then((s) => alive && setAll(s ?? {}))
      .catch((e) => { if (alive) { setSettingsErr(e); setAll({}); } });
    return () => { alive = false; };
  }, [tick]);

  async function act(label: string, fn: () => Promise<unknown>) {
    setActErr(null);
    setDone(null);
    try {
      await fn();
      setDone(label);
      chart.reload();
      setTick((t) => t + 1);
    } catch (e) {
      setActErr(e);
    }
  }

  const entity = { ...DEFAULT_ENTITY, ...((all?.entity as Partial<EntitySettings> | undefined) ?? {}) };
  const names = { ...DEFAULT_NAMES, ...((all?.members as Partial<MemberSettings> | undefined) ?? {}) };
  const map = useMemo<AccountMap>(() => {
    const m = (all?.account_map as Partial<AccountMap> | undefined) ?? {};
    return { ...DEFAULT_MAP, ...m, capital: { ...DEFAULT_MAP.capital, ...(m.capital ?? {}) }, distributions: { ...DEFAULT_MAP.distributions, ...(m.distributions ?? {}) } };
  }, [all]);

  return (
    <>
      {!canEdit && <ReadOnlyNote />}
      {done && <Note tone="good">{done}</Note>}
      <ErrorNote error={actErr} />
      <ErrorNote error={settingsErr} notConfigured="The ledger is not set up yet." />

      <Section title="Chart of accounts">
        <ErrorNote error={chart.error} />
        {chart.loading && chart.accounts.length === 0 ? <Empty>Loading…</Empty> : (
          <ChartTable accounts={chart.accounts} canEdit={canEdit} act={act} />
        )}
        <p className="lp-field-hint" style={{ marginTop: 10 }}>Line numbers follow the 2025 Form 1065. Check them against the current year's form.</p>
        <h3 className="lp-item-title" style={{ marginTop: 22 }}>Add an account</h3>
        <AddAccountForm accounts={chart.accounts} disabled={!canEdit} act={act} />
      </Section>

      <Section title="Entity">
        <EntityForm key={`e-${tick}-${all ? 1 : 0}`} entity={entity} disabled={!canEdit} act={act} />
      </Section>

      <Section title="Member names">
        <MemberNamesForm key={`m-${tick}-${all ? 1 : 0}`} names={names} disabled={!canEdit} act={act} />
      </Section>

      <Section title="Account map">
        <p className="lp-field-hint" style={{ marginTop: 0 }}>
          Which account each automatic posting uses. Saved by account code, so renaming an account does not break it.
        </p>
        <AccountMapForm key={`a-${tick}-${all ? 1 : 0}`} map={map} accounts={chart.accounts} byCode={chart.byCode} byId={chart.byId} disabled={!canEdit} act={act} />
      </Section>
    </>
  );
}

type Act = (label: string, fn: () => Promise<unknown>) => Promise<void>;

// ── Chart table with inline edit ─────────────────────────────────────────────
function ChartTable({ accounts, canEdit, act }: { accounts: Account[]; canEdit: boolean; act: Act }) {
  const [editing, setEditing] = useState<string | null>(null);
  const subtypes = useMemo(() => Array.from(new Set(accounts.map((a) => a.subtype))).sort(), [accounts]);
  if (accounts.length === 0) return <Empty>No accounts yet. The ledger migration seeds the chart.</Empty>;
  return (
    <>
      <datalist id="books-subtypes">{subtypes.map((s) => <option key={s} value={s} />)}</datalist>
      {TYPE_ORDER.map((t) => {
        const rows = accounts.filter((a) => a.type === t);
        if (rows.length === 0) return null;
        return (
          <div key={t} style={{ marginBottom: 18 }}>
            <h3 className="lp-item-title" style={{ margin: '14px 0 6px' }}>{ACCOUNT_TYPE_LABELS[t]}</h3>
            <div className="lp-tbl-wrap">
              <table className="lp-tbl">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Subtype</th>
                    <th>Normal</th>
                    <th>Member</th>
                    <th>Tax line</th>
                    <th className="num">Order</th>
                    <th>Active</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    editing === a.id
                      ? <EditRow key={a.id} account={a} onCancel={() => setEditing(null)} act={async (l, fn) => { await act(l, fn); setEditing(null); }} />
                      : (
                        <tr key={a.id} className={a.is_active ? '' : 'quiet'}>
                          <td className="lp-money">{a.code}</td>
                          <td>{a.name}{a.is_system && <> <span className="chip chip--info" title="Used by automatic postings. Cannot be deleted.">system</span></>}</td>
                          <td>{a.subtype.replace(/_/g, ' ')}</td>
                          <td>{a.normal_balance === 'debit' ? 'Debit' : 'Credit'}</td>
                          <td>{a.member ? memberWords(a.member) : '—'}</td>
                          <td>{a.tax_line ? TAX_LINE_LABELS[a.tax_line] : <span className="muted">Balance sheet</span>}</td>
                          <td className="num">{a.display_order}</td>
                          <td>{a.is_active ? <span className="lp-tag lp-tag--good">Active</span> : <span className="lp-tag">Inactive</span>}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <button type="button" className="lp-btn lp-btn--quiet lp-btn--small" disabled={!canEdit} onClick={() => setEditing(a.id)}>Edit</button>
                            {!a.is_system && (
                              <ConfirmButton
                                label="Delete"
                                tone="danger"
                                disabled={!canEdit}
                                sentence={`Delete ${a.code} ${a.name}. This only works when no entry has ever used it; otherwise mark it inactive.`}
                                yes="Yes, delete it"
                                onConfirm={() => act('Account deleted.', () => accountsApi.remove(a.id))}
                              />
                            )}
                          </td>
                        </tr>
                      )
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </>
  );
}

function EditRow({ account: a, onCancel, act }: { account: Account; onCancel: () => void; act: Act }) {
  const [name, setName] = useState(a.name);
  const [subtype, setSubtype] = useState(a.subtype);
  const [taxLine, setTaxLine] = useState<TaxLine | ''>(a.tax_line ?? '');
  const [active, setActive] = useState(a.is_active);
  const [order, setOrder] = useState(String(a.display_order));
  const [busy, setBusy] = useState(false);
  const ok = name.trim().length > 0 && subtype.trim().length > 0 && Number.isInteger(Number(order));
  return (
    <tr>
      <td className="lp-money">{a.code}</td>
      <td><input className="lp-inp" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" /></td>
      <td><input className="lp-inp" list="books-subtypes" value={subtype} onChange={(e) => setSubtype(e.target.value)} aria-label="Subtype" /></td>
      <td>{a.normal_balance === 'debit' ? 'Debit' : 'Credit'}</td>
      <td>{a.member ? memberWords(a.member) : '—'}</td>
      <td>
        <select className="lp-sel" value={taxLine} onChange={(e) => setTaxLine(e.target.value as TaxLine | '')} aria-label="Tax line">
          <option value="">Balance sheet (no line)</option>
          {TAX_LINES.map((t) => <option key={t} value={t}>{TAX_LINE_LABELS[t]}</option>)}
        </select>
      </td>
      <td className="num"><input className="lp-inp lp-money" inputMode="numeric" value={order} onChange={(e) => setOrder(e.target.value)} style={{ width: 90 }} aria-label="Display order" /></td>
      <td>
        <label className="lp-row" style={{ gap: 6 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active
        </label>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button
          type="button"
          className="lp-btn lp-btn--primary lp-btn--small"
          disabled={!ok || busy}
          onClick={async () => {
            setBusy(true);
            try {
              await act('Account saved.', () => accountsApi.update(a.id, { name: name.trim(), subtype: subtype.trim(), tax_line: taxLine || null, is_active: active, display_order: Number(order) }));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>{' '}
        <button type="button" className="lp-btn lp-btn--quiet lp-btn--small" disabled={busy} onClick={onCancel}>Cancel</button>
      </td>
    </tr>
  );
}

function AddAccountForm({ accounts, disabled, act }: { accounts: Account[]; disabled: boolean; act: Act }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<AccountType>('expense');
  const [subtype, setSubtype] = useState('');
  const [normal, setNormal] = useState<'auto' | NormalBalance>('auto');
  const [member, setMember] = useState<'' | Member>('');
  const [taxLine, setTaxLine] = useState<TaxLine | ''>('other_deductions');
  const [busy, setBusy] = useState(false);

  const codeTaken = accounts.some((a) => a.code === code.trim());
  const ok = !disabled && !busy && /^\d{3,6}$/.test(code.trim()) && !codeTaken && name.trim().length > 0 && subtype.trim().length > 0;
  const effectiveNormal = normal === 'auto' ? normalFor(type) : normal;

  return (
    <div className="lp-form" style={{ maxWidth: 760 }}>
      <div className="lp-form-grid">
        <Field label="Code" hint={codeTaken ? 'That code is already used.' : 'Numbers only, like 6350.'}>
          <input className="lp-inp lp-money" inputMode="numeric" value={code} disabled={disabled} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <Field label="Name"><input className="lp-inp" value={name} disabled={disabled} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Type">
          <select className="lp-sel" value={type} disabled={disabled} onChange={(e) => { const t = e.target.value as AccountType; setType(t); if (t === 'asset' || t === 'liability' || t === 'equity') setTaxLine(''); }}>
            {TYPE_ORDER.map((t) => <option key={t} value={t}>{ACCOUNT_TYPE_LABELS[t]}</option>)}
          </select>
        </Field>
        <Field label="Subtype" hint="Pick one that exists or type a new one.">
          <input className="lp-inp" list="books-subtypes" value={subtype} disabled={disabled} onChange={(e) => setSubtype(e.target.value)} />
        </Field>
        <Field label="Normal balance" hint={`Auto picks ${normalFor(type)} for ${ACCOUNT_TYPE_LABELS[type].toLowerCase()}.`}>
          <select className="lp-sel" value={normal} disabled={disabled} onChange={(e) => setNormal(e.target.value as 'auto' | NormalBalance)}>
            <option value="auto">Auto ({effectiveNormal})</option>
            <option value="debit">Debit</option>
            <option value="credit">Credit</option>
          </select>
        </Field>
        <Field label="Member" hint="Only for capital and distribution accounts.">
          <select className="lp-sel" value={member} disabled={disabled} onChange={(e) => setMember(e.target.value as '' | Member)}>
            <option value="">None</option>
            {MEMBERS.map((m) => <option key={m} value={m}>{memberWords(m)}</option>)}
          </select>
        </Field>
        <Field label="Tax line" hint="Balance sheet accounts have none.">
          <select className="lp-sel" value={taxLine} disabled={disabled} onChange={(e) => setTaxLine(e.target.value as TaxLine | '')}>
            <option value="">Balance sheet (no line)</option>
            {TAX_LINES.map((t) => <option key={t} value={t}>{TAX_LINE_LABELS[t]}</option>)}
          </select>
        </Field>
      </div>
      <div className="lp-row">
        <button
          type="button"
          className="lp-btn lp-btn--primary"
          disabled={!ok}
          onClick={async () => {
            setBusy(true);
            try {
              await act(`Account ${code.trim()} added.`, () => accountsApi.add({
                code: code.trim(), name: name.trim(), type, subtype: subtype.trim(),
                normal_balance: normal === 'auto' ? null : normal, member: member || null, tax_line: taxLine || null,
              }));
              setCode(''); setName(''); setSubtype('');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Adding…' : 'Add the account'}
        </button>
      </div>
    </div>
  );
}

// ── Entity ───────────────────────────────────────────────────────────────────
function EntityForm({ entity, disabled, act }: { entity: EntitySettings; disabled: boolean; act: Act }) {
  const [name, setName] = useState(entity.name ?? '');
  const [state, setState] = useState(entity.state ?? '');
  const [ein, setEin] = useState(entity.ein_last4 ?? '');
  const [fy, setFy] = useState(String(entity.fiscal_year_start ?? 1));
  const [busy, setBusy] = useState(false);
  const ok = !disabled && !busy && name.trim().length > 0 && (ein === '' || /^\d{4}$/.test(ein));
  return (
    <div className="lp-form" style={{ maxWidth: 760 }}>
      <div className="lp-form-grid">
        <Field label="Name"><input className="lp-inp" value={name} disabled={disabled} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="State" hint="Where the LLC is registered."><input className="lp-inp" value={state} disabled={disabled} onChange={(e) => setState(e.target.value)} placeholder="HI" /></Field>
        <Field label="EIN, last 4" hint="Only the last four digits are kept here."><input className="lp-inp lp-money" inputMode="numeric" maxLength={4} value={ein} disabled={disabled} onChange={(e) => setEin(e.target.value.replace(/\D/g, ''))} /></Field>
        <Field label="Fiscal year starts">
          <select className="lp-sel" value={fy} disabled={disabled} onChange={(e) => setFy(e.target.value)}>
            {MONTHS.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
          </select>
        </Field>
      </div>
      <p className="lp-field-hint">Tax form: {entity.tax_form ?? '1065'} (partnership return).</p>
      <div className="lp-row">
        <button
          type="button"
          className="lp-btn lp-btn--primary"
          disabled={!ok}
          onClick={async () => {
            setBusy(true);
            try {
              const next: EntitySettings = { name: name.trim(), tax_form: '1065', fiscal_year_start: Number(fy), state: state.trim() || null, ein_last4: ein || null };
              await act('Entity saved.', () => settings.set('entity', next));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Save entity'}
        </button>
      </div>
    </div>
  );
}

// ── Member names ─────────────────────────────────────────────────────────────
function MemberNamesForm({ names, disabled, act }: { names: MemberSettings; disabled: boolean; act: Act }) {
  const [leah, setLeah] = useState(names.leah?.name ?? 'Leah');
  const [laura, setLaura] = useState(names.laura?.name ?? 'Laura Means');
  const [busy, setBusy] = useState(false);
  const ok = !disabled && !busy && leah.trim().length > 0 && laura.trim().length > 0;
  return (
    <div className="lp-form" style={{ maxWidth: 760 }}>
      <div className="lp-form-grid">
        <Field label="Leah" hint="As it should print on statements."><input className="lp-inp" value={leah} disabled={disabled} onChange={(e) => setLeah(e.target.value)} /></Field>
        <Field label="Laura" hint="As it should print on statements."><input className="lp-inp" value={laura} disabled={disabled} onChange={(e) => setLaura(e.target.value)} /></Field>
      </div>
      <p className="lp-field-hint">Percentages live with the Apple split settings and are not changed here.</p>
      <div className="lp-row">
        <button
          type="button"
          className="lp-btn lp-btn--primary"
          disabled={!ok}
          onClick={async () => {
            setBusy(true);
            try {
              const next: MemberSettings = { leah: { name: leah.trim() }, laura: { name: laura.trim() } };
              await act('Member names saved.', () => settings.set('members', next));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Save names'}
        </button>
      </div>
    </div>
  );
}

// ── Account map (stored by code; chosen by account) ──────────────────────────
function AccountMapForm({ map, accounts, byCode, byId, disabled, act }: {
  map: AccountMap; accounts: Account[]; byCode: Map<string, Account>; byId: Map<string, Account>; disabled: boolean; act: Act;
}) {
  const [draft, setDraft] = useState<AccountMap>(map);
  const [busy, setBusy] = useState(false);
  const idFor = (code: string) => byCode.get(code)?.id ?? '';
  const codeFor = (id: string) => byId.get(id)?.code ?? '';
  const missing = [
    ...MAP_ROWS.map((r) => draft[r.key]),
    ...MEMBERS.map((m) => draft.capital[m]),
    ...MEMBERS.map((m) => draft.distributions[m]),
  ].filter((code) => code && !byCode.has(code));

  function Row({ label, hint, code, onCode }: { label: string; hint: string; code: string; onCode: (c: string) => void }) {
    return (
      <tr>
        <td><strong>{label}</strong><div className="lp-field-hint">{hint}</div></td>
        <td>
          <AccountSelect value={idFor(code)} onChange={(id) => onCode(codeFor(id))} accounts={accounts} disabled={disabled} activeOnly={false} />
        </td>
        <td className="lp-money">{code || '—'}{code && !byCode.has(code) && <> <span className="lp-tag lp-tag--bad">No account with this code</span></>}</td>
      </tr>
    );
  }

  return (
    <>
      <div className="lp-tbl-wrap">
        <table className="lp-tbl">
          <thead><tr><th>Used for</th><th style={{ minWidth: 280 }}>Account</th><th>Code</th></tr></thead>
          <tbody>
            {MAP_ROWS.map((r) => (
              <Row key={r.key} label={r.label} hint={r.hint} code={draft[r.key]} onCode={(c) => setDraft((d) => ({ ...d, [r.key]: c }))} />
            ))}
            {MEMBERS.map((m) => (
              <Row key={`cap-${m}`} label={`Member capital, ${memberWords(m)}`} hint="Contributions and allocated income." code={draft.capital[m]} onCode={(c) => setDraft((d) => ({ ...d, capital: { ...d.capital, [m]: c } }))} />
            ))}
            {MEMBERS.map((m) => (
              <Row key={`dist-${m}`} label={`Member distributions, ${memberWords(m)}`} hint="Payouts and other draws." code={draft.distributions[m]} onCode={(c) => setDraft((d) => ({ ...d, distributions: { ...d.distributions, [m]: c } }))} />
            ))}
          </tbody>
        </table>
      </div>
      {missing.length > 0 && <Note tone="warn">{missing.length} {missing.length === 1 ? 'code points' : 'codes point'} at no account. Pick an account for each before saving.</Note>}
      <div className="lp-row" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="lp-btn lp-btn--primary"
          disabled={disabled || busy || missing.length > 0}
          onClick={async () => {
            setBusy(true);
            try {
              await act('Account map saved.', () => settings.set('account_map', draft));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Save account map'}
        </button>
        <button type="button" className="lp-btn lp-btn--quiet" disabled={busy} onClick={() => setDraft(map)}>Reset</button>
      </div>
    </>
  );
}
